import React, { useCallback, useEffect } from "react";
import { useSelector } from "react-redux";
import { useAppDispatch } from "../hooks/reduxHooks";
import { RootState } from "../redux/store";
import { useHistory, useLocation } from "react-router-dom";
import { parse } from "query-string";
import PageLoader from "../components/page-loader";
import { login, setSelectedLanguage } from "../redux/actions/authActions";
import { moreThan5Weeks } from "../utils";
import { getSurveyTypeByPainArea } from "../utils/surveyUtils";
import { getVerificationPermissions, isMHIPAvailable } from "../routes/dashboard/helpers";
import i18n from "../i18n";
import { useUnleashFlag } from "../hooks/useUnleashFlag";

const adminCompatibleViews = ["/exercises/"];

const Restricted = BaseComponent => {
  const AdminComponent = props => {
    const dispatch = useAppDispatch();
    const history = useHistory();
    const { pathname } = useLocation();
    const { isAuthenticated, user = {}, attempts, isLoading } = useSelector((state: RootState) => state.auth);
    const isNewUI = useUnleashFlag("new-ui");

    function getIsAdminCompatible(pathName) {
      let result = false;
      adminCompatibleViews.forEach(el => {
        if (pathName.startsWith(el)) result = true;
      });
      return result;
    }

    const isAdminCompatible = getIsAdminCompatible(pathname);

    const checkPermissions = useCallback(async () => {
      if (!isAuthenticated) {
        history.push("/login");
        return;
      }

      if (isAuthenticated && user && user["custom:permissionLevel"] === "3") {
        if (isAdminCompatible) {
          return;
        } else if (!isAdminCompatible) {
          history.push("/import-users");
          return;
        }
      }

      if (isAuthenticated && user && user["custom:isAdmin"] === "true") {
        if (isAdminCompatible) {
          return;
        } else if (!isAdminCompatible) {
          history.push("/admin");
          return;
        }
      }

      if (
        isAuthenticated &&
        user?.summary !== undefined &&
        !user.summary.mainPainAreaId &&
        user.summary.intakeCompleted === undefined &&
        !user.summary.applicationSubmittedTimestamp &&
        !user.summary.email
      ) {
        history.push("/plan-selection");
        return;
      }

      const storedNoFitTs = Number(localStorage.getItem("rkNoFitTimestamp") || 0);
      const isInProgress = user?.summary?.status === "IN_PROGRESS";
      const hasMultiplePainAreas = (user?.summary?.painAreaList?.length ?? 0) > 1;
      if (isInProgress) {
        if (storedNoFitTs > 0) localStorage.removeItem("rkNoFitTimestamp");
      } else if (!hasMultiplePainAreas) {
        if (storedNoFitTs > 0) {
          if (!moreThan5Weeks(storedNoFitTs)) {
            history.push("/intake-survey/no-fit");
            return;
          }
          localStorage.removeItem("rkNoFitTimestamp");
        } else if (user?.summary?.noChronicPain) {
          const ts = user?.summary?.applicationSubmittedTimestamp;
          const isLikelyUnixTs = ts && ts > 1_000_000_000;
          if (!isLikelyUnixTs || !moreThan5Weeks(ts)) {
            history.push("/intake-survey/no-fit");
            return;
          }
        }
      }

      if (isAuthenticated && !hasMultiplePainAreas && !user?.summary?.applicationSubmittedTimestamp && !user?.summary?.intakeCompleted) {
        history.push("/plan-selection");
        return;
      }

      if (isAuthenticated && user?.summary?.status === "POST-GRADUATED") {
        history.push("/postgraduate");
        return;
      }

      if (
        isAuthenticated &&
        (user?.summary?.applicantArchived || user?.summary?.status === "DROPPED_OUT" || user?.summary?.status === "NOT_ACCEPTED")
      ) {
        history.push("/intake-process-selection");
        return;
      }

      if (isAuthenticated && user?.summary?.status === "POST-DROPPED_OUT") {
        history.push("/postDroppedOut");
        return;
      }

      if (isAuthenticated && !user?.summary?.intakeCompleted && !(user?.summary?.painAreaList?.length > 1)) {
        const painAreaSelected = localStorage.getItem("rkPainAreaSelected");
        if (user?.summary.status === "IN_PROGRESS" && user?.summary?.painArea === "prevention") {
          return;
        }

        const determinePainAreaAndSurveyType = async () => {
          const painArea = painAreaSelected || user?.summary?.painArea;
          const surveyType = await getSurveyTypeByPainArea(painArea);

          if (painAreaSelected) {
            history.push(`/survey/${painAreaSelected}/${surveyType}`);
          } else if (isAuthenticated && !user?.summary?.applicationSubmittedTimestamp && !user?.summary?.intakeCompleted) {
            history.push("/plan-selection");
          } else {
            history.push(`/survey/${user?.summary?.painArea}/${surveyType}`);
          }
        };

        determinePainAreaAndSurveyType();
        return;
      }

      if (isAuthenticated && user && (user.summary?.additionalTerms === "Pending" || user.summary?.additionalTerms === "Declined")) {
        history.push("/additionalTerms");
        return;
      }

      if (isAuthenticated && (pathname.startsWith("/mhip/programs-selection") || pathname === "/library")) {
        const permissions = getVerificationPermissions(user?.summary);
        const hasMHIP = isMHIPAvailable(user?.summary);
        const canAccessLibrary = isNewUI ? hasMHIP : permissions.programLibrary.visible || hasMHIP;
        if (!canAccessLibrary) {
          history.push("/");
          return;
        }
      }

      return;
    }, [history, isAuthenticated, user, isAdminCompatible, pathname, isNewUI]);

    useEffect(() => {
      if (attempts > 0 && !isLoading) checkPermissions();
    }, [attempts, checkPermissions, isLoading]);

    useEffect(() => {
      const lang = user?.summary?.lang;
      if (lang) {
        i18n.changeLanguage(lang);
        if (lang !== localStorage.getItem("rkLanguage")) {
          dispatch(setSelectedLanguage(lang));
        }
      }
    }, [user?.summary?.lang, dispatch]);

    useEffect(() => {
      if (isAuthenticated) return;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const searchObject: any = parse(window.location.search);
      const { consentAuthKey, email } = searchObject;
      const isExtLoginInProgress = !!(consentAuthKey && email);
      if (isExtLoginInProgress) return;
      if (!localStorage.getItem("rkAccessToken")) {
        history.push("/login");
        return;
      }
      const timer = setTimeout(() => dispatch(login()), 100);
      return () => clearTimeout(timer);
    }, [dispatch, isAuthenticated, history]);

    if (isAuthenticated) return <BaseComponent {...props} />;
    return <PageLoader context="login" />;
  };
  return AdminComponent;
};

export { Restricted };
